import { useAppDispatch, useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { LEVEL_TYPE } from "api/interfaces/commonInterface.interface";

import { useCallback, useEffect, useMemo } from "react";
import { useQuery } from "react-query";
import { getDiscountTier, getService } from "api/subscriptionAPI";
import {
  BILLING_CYCLE,
  GetPricingResponse,
  GetServiceResponse,
  KeyCost,
  KeyInfo,
  SUBSCRIPTION_STATUS,
  SUBSCRIPTION_TYPE,
  SubscriptionService,
} from "api/interfaces/subscription.interface";
import { initialKeyTypeOptions, Option } from "utils/options";
import { showActivateStatus } from "utils/SubscriptionUtil";
import { useFlag } from "@unleash/proxy-client-react";

type CalculatePriceParams = {
  channels: number;
  msrp: number;
  qty: number;
  discount: number;
};

export const changeServiceName = (type: SUBSCRIPTION_TYPE) => {
  switch (type) {
    case SUBSCRIPTION_TYPE.STANDARD:
      return "myDW Standard Subscription";
    case SUBSCRIPTION_TYPE.STORAGE:
      return "myDW Cloud Storage";
    default:
      return "unknown";
  }
};

export const changeServiceNameUnit = (type: SUBSCRIPTION_TYPE | undefined) => {
  switch (type) {
    case SUBSCRIPTION_TYPE.STANDARD:
      return "Number of Channels";
    case SUBSCRIPTION_TYPE.STORAGE:
      return "Capacity (TB)";
    default:
      return "";
  }
};

export const showCost = (cost?: number) => {
  return (cost || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, //.뒷자리의 최소갯수
    maximumFractionDigits: 2, //.뒷자리를 최대갯수
  });
};

export function useKeyType() {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const isUnleashEnabled_keyDurationDays = useFlag(
    "feature_subscription_key_duration_days"
  );
  const isUnleashEnabled_planCycle3day = useFlag(
    "feature_subscription_planCycle_3day"
  );

  const isUnleashEnabled_planCycle5day = useFlag(
    "feature_subscription_planCycle_5day"
  );

  const getServiceData = useQuery(["getService"], () => getService({}), {
    refetchOnWindowFocus: false,
    select: (res: GetServiceResponse) => {
      if (res.error === 0 && res.result) {
        return res.result;
      }
    },
  });

  const getDiscountTierData = useQuery(
    ["getDiscountTier"],
    () => getDiscountTier({}),
    {
      refetchOnWindowFocus: false,
      select: (res: GetPricingResponse) => {
        if (res.error === 0 && res.result) {
          return res.result;
        }
      },
    }
  );

  // console.log(selectedAccount, getDiscountTierData.data);

  const keyTypeOptions: Option[] = useMemo(() => {
    if (getServiceData.data) {
      return getServiceData.data.map((ser: SubscriptionService) => {
        return {
          label: changeServiceName(ser.type),
          value: ser.type,
        };
      });
    }
    return initialKeyTypeOptions;
  }, [getServiceData.data]);

  const subscriptionServiceType: SubscriptionService[] = useMemo(() => {
    if (getServiceData.data) {
      return getServiceData.data;
    }
    return [];
  }, [getServiceData.data]);

  const changeStringToStatusOption = useCallback(
    (status: SUBSCRIPTION_STATUS) => {
      let nonLabel = "No Registered";

      if (selectedAccount.accountLevel === LEVEL_TYPE.CP) {
        nonLabel = "No Activated";
      }

      if (status === SUBSCRIPTION_STATUS.NONE) {
        return nonLabel;
      } else {
        return showActivateStatus(status);
      }
    },
    [selectedAccount]
  );
  const statusOptions = (() => {
    return Object.values(SUBSCRIPTION_STATUS)
      .map((stat) => {
        return {
          label: changeStringToStatusOption(stat),
          value: stat,
        };
      })
      .filter((filterStat) => {
        return !filterStat.value.includes("EXPIRED");
      });
  })();

  const calculateTotalCost = ({
    keyType,
    duration,
    number,
    qty,
  }: KeyInfo): KeyCost => {
    const findDiscountMsrp = subscriptionServiceType.find(
      (type) => type.type === keyType
    );
    const DISCOUNT_PERCENT = 40;

    if (!findDiscountMsrp || !duration || !number || !qty) {
      return { monthlyCost: 0, totalCost: 0 };
    }

    const originalPrice =
      (duration / 12) * number * qty * findDiscountMsrp.msrp;
    const discountedPrice = originalPrice * (1 - DISCOUNT_PERCENT / 100);
    const monthlyCost = discountedPrice / duration;

    return {
      totalCost: Math.floor(discountedPrice * 100) / 100,
      monthlyCost: Math.floor(monthlyCost * 100) / 100,
    };
  };

  const durationMonthOptions = (
    type: SUBSCRIPTION_TYPE,
    isDw: boolean = false
  ): Option[] => {
    const daysOptions: Option[] = [
      { label: "1 Day", value: "-1" },
      { label: "2 Days", value: "-2" },
    ];

    const baseOptions: Option[] = [
      { label: "1 Year (365 Days)", value: "12" },
      { label: "2 Years (730 Days)", value: "24" },
      { label: "3 Years (1,095 Days)", value: "36" },
    ];

    if (isDw) {
      return [
        ...(isUnleashEnabled_keyDurationDays &&
        type === SUBSCRIPTION_TYPE.STANDARD
          ? daysOptions
          : []),
        { label: "1 Month (30 Days)", value: "1" },
        { label: "2 Months (60 Days)", value: "2" },
        { label: "3 Months (90 Days)", value: "3" },
        { label: "6 Months (180 Days)", value: "6" },
        ...baseOptions,
      ];
    }

    return baseOptions;
  };

  const billingCycleOptions: Option[] = useMemo(() => {
    return [
      ...(isUnleashEnabled_planCycle3day
        ? [{ label: "2 Days", value: BILLING_CYCLE.TEST_2DAY }]
        : []),

      { label: "Monthly", value: BILLING_CYCLE.MONTHLY },
      ...(isUnleashEnabled_planCycle5day
        ? [{ label: "3 Days", value: BILLING_CYCLE.TEST_YEARLY }]
        : []),
      { label: "Yearly", value: BILLING_CYCLE.YEARLY },
    ];
  }, [isUnleashEnabled_planCycle3day]);

  return {
    subscriptionServiceType,
    keyTypeOptions,
    changeStringToStatusOption,
    statusOptions,
    calculateTotalCost,
    durationMonthOptions,
    billingCycleOptions,
    getServiceData,
  };
}
